import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { Outlet, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { logoutUser } from '../../../store/actions/AuthActions';
import { fetchMySubscription } from '../../../store/actions/SubscriptionActions';
import NotificationBell from '../../components/Common/NotificationBell';
import VodService from '../../../services/VodService';
import { getNav, ALL_NAV_KEYS } from './navModel';
import defaultLogo from '../../../images/teve-logo-wide.png';
import { useBranding } from '../../../context/BrandingContext';

const STREAM_MODE_KEY = 'stream_mode';
const STREAM_MODE_EVENT = 'stream-mode-changed';
const COLLAPSE_KEY = 'console_sidebar_collapsed';
const NAV_CACHE_KEY = 'teve_nav_items';
const API_URL = process.env.REACT_APP_API_URL || '/api';
export const FAILOVER_CHANGED_EVENT = 'teve:failover-changed';

const getStoredMode = () =>
  localStorage.getItem(STREAM_MODE_KEY) === 'playlist' ? 'playlist' : 'live';

const titleFromPath = (pathname, nav) => {
  for (const group of nav) {
    for (const item of group.items) {
      if (item.to === pathname) return item.title;
      if (item.children?.some((c) => c.to === pathname)) {
        return item.children.find((c) => c.to === pathname).title;
      }
    }
  }
  if (pathname.startsWith('/streams/')) return 'Stream Detail';
  if (pathname.startsWith('/tickets/')) return 'Ticket';
  return 'Dashboard';
};

const NavItem = ({ item, collapsed, currentPath }) => {
  const hasChildren = !!item.children?.length;
  const childActive = hasChildren && item.children.some((c) => currentPath === c.to);
  const [open, setOpen] = useState(childActive);

  useEffect(() => {
    if (childActive) setOpen(true);
  }, [childActive]);

  if (!hasChildren) {
    return (
      <NavLink
        to={item.to}
        className={({ isActive }) => `console-link ${isActive ? 'active' : ''}`}
        title={item.title}
      >
        <span className="ico"><i className={`fa-solid ${item.icon}`} /></span>
        <span className="label">{item.title}</span>
      </NavLink>
    );
  }

  return (
    <div>
      <button
        type="button"
        className={`console-link ${childActive ? 'active' : ''}`}
        onClick={() => setOpen((o) => !o)}
        title={item.title}
        style={{ width: '100%', background: 'transparent', border: 0 }}
      >
        <span className="ico"><i className={`fa-solid ${item.icon}`} /></span>
        <span className="label" style={{ flex: 1, textAlign: 'left' }}>{item.title}</span>
        <span className="chev">
          <i className={`fa-solid fa-chevron-${open ? 'down' : 'right'}`} style={{ fontSize: 10 }} />
        </span>
      </button>
      {open && !collapsed && (
        <div className="console-subwrap">
          {item.children.map((c) => (
            <NavLink
              key={c.to}
              to={c.to}
              className={({ isActive }) => `console-sublink ${isActive ? 'active' : ''}`}
            >
              {c.title}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
};

const ConsoleShell = () => {
  const branding = useBranding();
  const logo = branding.custom && branding.logoUrl ? branding.logoUrl : defaultLogo;
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const user = useSelector((s) => s.auth.user);
  const subscription = useSelector((s) => s.subscription?.subscription);

  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(COLLAPSE_KEY) === '1'
  );
  const [mobileOpen, setMobileOpen] = useState(false);
  const [streamMode, setStreamMode] = useState(getStoredMode);
  const [userMenu, setUserMenu] = useState(false);
  const [failover, setFailover] = useState({ on: false, available: false, busy: false });

  // Per-plan sidebar allow-list. null = show everything (admin, or plan not
  // customized). Cached in sessionStorage so the menu doesn't flash on nav.
  const [allowedNav, setAllowedNav] = useState(() => {
    try {
      const c = JSON.parse(sessionStorage.getItem(NAV_CACHE_KEY) || 'null');
      return Array.isArray(c) ? c : null;
    } catch (_) { return null; }
  });

  useEffect(() => {
    if (!user) return;
    if (user.role === 'admin') { setAllowedNav(null); return; }
    let alive = true;
    const token = localStorage.getItem('streamToken');
    axios.get(`${API_URL}/nav`, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then(({ data }) => {
        if (!alive) return;
        // nav_items is already the fully-resolved allow-list when the admin
        // has customized this plan's sidebar. When they haven't (nav_items
        // null = "not customized"), the plan's actual entitlements can still
        // deny specific keys (denied_keys) — turn that into an explicit
        // allow-list too, rather than leaving it as null/"show everything",
        // which is exactly the bug that let e.g. "Live Switcher" show up for
        // a plan with allow_switcher=0.
        const denied = Array.isArray(data?.denied_keys) ? data.denied_keys : [];
        const items = Array.isArray(data?.nav_items)
          ? data.nav_items
          : (denied.length ? ALL_NAV_KEYS.filter((k) => !denied.includes(k)) : null);
        setAllowedNav(items);
        try {
          if (items) sessionStorage.setItem(NAV_CACHE_KEY, JSON.stringify(items));
          else sessionStorage.removeItem(NAV_CACHE_KEY);
        } catch (_) {}
      })
      .catch(() => { /* fail open — keep whatever we have */ });
    return () => { alive = false; };
  }, [user]);

  const nav = useMemo(
    () => getNav(user?.role, user?.role === 'admin' ? null : allowedNav),
    [user?.role, allowedNav]
  );
  const pageName = titleFromPath(location.pathname, nav);

  useEffect(() => {
    setMobileOpen(false);
    setUserMenu(false);
  }, [location.pathname]);

  useEffect(() => {
    const sync = (e) => {
      const mode = e?.detail?.mode;
      setStreamMode(mode === 'live' || mode === 'playlist' ? mode : getStoredMode());
    };
    window.addEventListener(STREAM_MODE_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(STREAM_MODE_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  useEffect(() => {
    if (user && user.role !== 'admin') dispatch(fetchMySubscription());
  }, [user, dispatch]);

  // Account-wide auto-failover toggle, shown in the topbar next to Live/Playlist.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    VodService.getFailoverSettings()
      .then((res) => {
        // `allowed` is false on plans without failover — keep the toggle hidden.
        if (!cancelled && res?.success && res.allowed !== false) {
          setFailover((f) => ({ ...f, on: !!res.enabled, available: true }));
        }
      })
      .catch(() => { /* no VOD access — leave hidden */ });
    const sync = (e) => {
      if (typeof e?.detail?.on === 'boolean') setFailover((f) => ({ ...f, on: e.detail.on, available: true }));
    };
    window.addEventListener(FAILOVER_CHANGED_EVENT, sync);
    return () => { cancelled = true; window.removeEventListener(FAILOVER_CHANGED_EVENT, sync); };
  }, [user]);

  const toggleFailover = useCallback(async () => {
    setFailover((f) => {
      if (f.busy) return f;
      const next = !f.on;
      VodService.setFailoverSettings(next)
        .then((res) => {
          const ok = res?.success !== false;
          setFailover((cur) => ({ ...cur, on: ok ? next : cur.on, busy: false }));
          if (ok) window.dispatchEvent(new CustomEvent(FAILOVER_CHANGED_EVENT, { detail: { on: next } }));
        })
        .catch(() => setFailover((cur) => ({ ...cur, busy: false })));
      return { ...f, on: next, busy: true };
    });
  }, []);

  const toggleCollapse = useCallback(() => {
    setCollapsed((c) => {
      localStorage.setItem(COLLAPSE_KEY, c ? '0' : '1');
      return !c;
    });
  }, []);

  const setMode = (mode) => {
    setStreamMode(mode);
    localStorage.setItem(STREAM_MODE_KEY, mode);
    window.dispatchEvent(new CustomEvent(STREAM_MODE_EVENT, { detail: { mode } }));
  };

  const initials = (user?.username || user?.email || 'U')
    .split(/[\s@.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join('');

  const planLabel = subscription?.plan?.name || subscription?.plan_name;

  return (
    <div className={`console ${collapsed ? 'collapsed' : ''} ${mobileOpen ? 'mobile-open' : ''}`}>
      <div className="console-scrim" onClick={() => setMobileOpen(false)} />

      <aside className="console-sidebar">
        <div className="console-brand">
          <img src={logo} alt={branding.brandName} onError={(e) => { e.target.src = defaultLogo; }} />
        </div>
        <nav className="console-nav">
          {nav.map((group) => (
            <div key={group.section}>
              <div className="console-nav-section">{group.section}</div>
              {group.items.map((item) => (
                <NavItem
                  key={item.title}
                  item={item}
                  collapsed={collapsed}
                  currentPath={location.pathname}
                />
              ))}
            </div>
          ))}
        </nav>
        {!collapsed && (
          <div className="console-sidebar-foot">
            Powered by{' '}
            <a href="https://siman.ca" target="_blank" rel="noreferrer">Siman Tech</a>
          </div>
        )}
      </aside>

      <div className="console-main">
        <header className="console-topbar">
          <button
            className="console-iconbtn d-lg-none"
            onClick={() => setMobileOpen((o) => !o)}
            aria-label="Menu"
          >
            <i className="fa-solid fa-bars" />
          </button>
          <button
            className="console-iconbtn d-none d-lg-inline-flex"
            onClick={toggleCollapse}
            aria-label="Collapse sidebar"
          >
            <i className={`fa-solid fa-angles-${collapsed ? 'right' : 'left'}`} />
          </button>

          <div className="page-name">{pageName}</div>

          <div className="console-search d-none d-md-flex">
            <i className="fa-solid fa-magnifying-glass" style={{ fontSize: 13 }} />
            <input
              placeholder="Search streams, VODs, settings…"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && e.target.value.trim()) {
                  navigate(`/streams?q=${encodeURIComponent(e.target.value.trim())}`);
                }
              }}
            />
          </div>

          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
            <div className="tw-hidden md:tw-flex" style={{ display: 'flex', gap: 3, padding: 3, background: 'var(--c-surface-3)', borderRadius: 10 }}>
              {['live', 'playlist'].map((m) => (
                <button
                  key={m}
                  onClick={() => setMode(m)}
                  style={{
                    padding: '5px 12px',
                    fontSize: 12,
                    fontWeight: 600,
                    border: 0,
                    borderRadius: 8,
                    cursor: 'pointer',
                    background: streamMode === m ? 'var(--c-brand)' : 'transparent',
                    color: streamMode === m ? '#fff' : 'var(--c-muted)',
                  }}
                >
                  {m === 'live' ? 'Live' : 'Playlist'}
                </button>
              ))}
            </div>

            {failover.available && (
              <button
                type="button"
                onClick={toggleFailover}
                disabled={failover.busy}
                title="Auto-failover: if the live stream drops, shuffle-play your VOD library after a short grace period"
                className="tw-hidden md:tw-inline-flex"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 7,
                  padding: '7px 11px', fontSize: 12, fontWeight: 600,
                  border: 0, borderRadius: 10, cursor: 'pointer',
                  background: 'var(--c-surface-3)', color: 'var(--c-muted)',
                  opacity: failover.busy ? 0.6 : 1,
                }}
              >
                <i className="fa-solid fa-shield-halved" style={{ fontSize: 11 }} />
                Failover
                <span
                  aria-hidden
                  style={{
                    position: 'relative', width: 30, height: 16, borderRadius: 999,
                    background: failover.on ? 'var(--c-brand)' : 'rgba(107,114,136,0.45)',
                    transition: 'background .15s', flexShrink: 0,
                  }}
                >
                  <span style={{
                    position: 'absolute', top: 2, left: failover.on ? 16 : 2, width: 12, height: 12,
                    borderRadius: '50%', background: '#fff', transition: 'left .15s',
                  }} />
                </span>
                <span style={{ color: failover.on ? 'var(--c-brand)' : 'var(--c-faint)' }}>
                  {failover.on ? 'ON' : 'OFF'}
                </span>
              </button>
            )}

            <div className="nav-item dropdown notification_dropdown">
              <NotificationBell />
            </div>

            <div style={{ position: 'relative' }}>
              <button className="console-user" onClick={() => setUserMenu((o) => !o)}>
                <span className="av">{initials}</span>
                <span className="d-none d-md-inline" style={{ fontSize: 13, fontWeight: 600, color: 'var(--c-text)' }}>
                  {user?.username || user?.email || 'Account'}
                </span>
                <i className="fa-solid fa-chevron-down" style={{ fontSize: 10, color: 'var(--c-faint)' }} />
              </button>
              {userMenu && (
                <div
                  className="dropdown-menu show"
                  style={{ position: 'absolute', right: 0, top: 'calc(100% + 8px)', minWidth: 200 }}
                >
                  <div style={{ padding: '6px 12px 8px' }}>
                    <div style={{ fontWeight: 700, color: '#fff', fontSize: 13 }}>
                      {user?.username || 'User'}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--c-faint)' }}>{user?.email}</div>
                    {planLabel && (
                      <span className="badge bg-secondary" style={{ marginTop: 6 }}>{planLabel} plan</span>
                    )}
                  </div>
                  <div className="dropdown-divider" />
                  <button className="dropdown-item" onClick={() => navigate('/billing')}>
                    <i className="fa-solid fa-credit-card me-2" /> Billing
                  </button>
                  <button className="dropdown-item" onClick={() => navigate('/change-password')}>
                    <i className="fa-solid fa-key me-2" /> Change Password
                  </button>
                  <div className="dropdown-divider" />
                  <button
                    className="dropdown-item text-danger"
                    onClick={() => dispatch(logoutUser(navigate))}
                  >
                    <i className="fa-solid fa-arrow-right-from-bracket me-2" /> Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        <main className="console-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default ConsoleShell;
